import { partialMatchKey, type QueryKey } from "@tanstack/react-query";
import { queryClient } from "./queryClient";
import { userInfoKeys } from "./userInfo";

const DEFAULT_BACKGROUND_KEYS = [userInfoKeys.user()];
type DefaultBackgroundKey = (typeof DEFAULT_BACKGROUND_KEYS)[number];

type InvalidateAllOptions = {
  /** Additional keys to invalidate without awaiting — refetch happens in the background. */
  background?: Array<QueryKey>;
  /** Force a default-background key to be awaited instead. Must match an entry in `DEFAULT_BACKGROUND_KEYS`. */
  blocking?: Array<DefaultBackgroundKey>;
  /** Keys to skip entirely — neither marked stale nor refetched. Takes precedence over background/blocking. */
  ignore?: Array<QueryKey>;
};

const matchesAny = (queryKey: QueryKey, prefixes: Array<QueryKey>) =>
  prefixes.some(prefix => partialMatchKey(queryKey, prefix));

/**
 * Invalidate every cached query by default, with three opt-outs:
 *  - `background`: invalidated but not awaited (caller doesn't block on refetch).
 *    `DEFAULT_BACKGROUND_KEYS` (currently `userInfoKeys.user()`) is always added — it's
 *    heavy and rarely changes from a feature mutation, but we still want eventual consistency.
 *  - `blocking`: forces a default-backgrounded key to be awaited. Use when a mutation
 *    actually changes that data (e.g. role/org switch) and the caller must wait for it.
 *  - `ignore`: not invalidated at all. Takes precedence over `background` and `blocking`.
 *
 * All lists prefix-match: `[["informationTypes"]]` matches every `["informationTypes", ...]` key.
 *
 * Returns the promise for the blocking invalidation — return it from `onSuccess` so
 * `mutateAsync` resolves only once those refetches finish.
 */
export const invalidateAll = ({ background = [], blocking = [], ignore = [] }: InvalidateAllOptions = {}) => {
  const backgroundKeys = [...DEFAULT_BACKGROUND_KEYS, ...background].filter(
    key => !matchesAny(key, ignore) && !matchesAny(key, blocking)
  );

  for (const queryKey of backgroundKeys) {
    void queryClient.invalidateQueries({ queryKey });
  }
  return queryClient.invalidateQueries({
    predicate: query => !matchesAny(query.queryKey, backgroundKeys) && !matchesAny(query.queryKey, ignore),
  });
};
